'use client'; // NextJS 13 requires this. Remove if you are using NextJS 12 or lower
import { useEffect, useState } from 'react';
import Script from 'next/script';
import { useTrackClientEvent } from '@analytics/trackClientEvent';

interface FeaturebaseUser {
  id?: string;
  email?: string;
  name?: string;
  isAdmin?: boolean;
  subscriptionPlan?: string;
  createdAt?: string;
}

interface FeaturebaseMessengerProps {
  user?: FeaturebaseUser;
}

const FeaturebaseMessenger = ({ user }: FeaturebaseMessengerProps) => {
  const [userHash, setUserHash] = useState<string | null>(null);
  const [hashFetched, setHashFetched] = useState(false);
  const { trackEvent } = useTrackClientEvent();

  // Fetch userHash when user is available
  useEffect(() => {
    if (user?.email && !hashFetched) {
      fetchUserHash();
    }
  }, [user, hashFetched]);

  const fetchUserHash = async () => {
    try {
      const response = await fetch('/api/featurebase/user-hash', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setUserHash(data.userHash);
      } else {
        console.warn('Failed to fetch Featurebase userHash:', response.status);
      }
    } catch (error) {
      console.error('Error fetching Featurebase userHash:', error);
    } finally {
      setHashFetched(true);
    }
  };

  useEffect(() => {
    // Only initialize Featurebase when we have either no user or have the userHash
    if (!user || (user && hashFetched)) {
      initializeFeaturebase();
    }
  }, [user, userHash, hashFetched]);

  const initializeFeaturebase = () => {
    const win = window;

    // Initialize Featurebase if it doesn't exist
    if (typeof win.Featurebase !== 'function') {
      win.Featurebase = function () {
        (win.Featurebase.q = win.Featurebase.q || []).push(arguments);
      };
    }

    // Prepare user configuration
    const userConfig: Record<string, any> = {
      appId: '6875313e9f9d9502fe3a22ce', // required
      theme: 'light', // "light" or "dark"
      language: 'en' // short code (e.g. "en", "de", etc.)
    };

    // Add user information if available
    if (user) {
      if (user.email) {
        userConfig.email = user.email;
      }
      if (user.name) {
        console.log('user.name', user.name);
        userConfig.name = user.name;
      }
      if (user.id) {
        userConfig.userId = user.id;
      }
      if (user.subscriptionPlan) {
        userConfig.subscriptionPlan = user.subscriptionPlan;
      }
      if (user.isAdmin !== undefined) {
        userConfig.isAdmin = user.isAdmin;
      }
      if (user.createdAt) {
        userConfig.createdAt = user.createdAt;
      }

      // Add userHash for identity verification
      if (userHash) {
        userConfig.userHash = userHash;
      }
    }

    // Boot Featurebase messenger with configuration
    win.Featurebase('boot', userConfig);

    // Set up event tracking after SDK is fully initialized
    setTimeout(() => {
      setupEventTracking();
    }, 2000);
  };

  const setupEventTracking = () => {
    const win = window;

    // Check if Featurebase SDK is ready
    if (!win.Featurebase || typeof win.Featurebase !== 'function') {
      console.warn('Featurebase SDK not ready, retrying...');
      setTimeout(setupEventTracking, 1000);
      return;
    }

    try {
      // Track when the messenger is shown (official API method)
      win.Featurebase('onShow', () => {
        console.log('Featurebase messenger was shown');
        trackConversationEvent('chat');
      });

      // Track when the messenger is hidden (for additional analytics)
      win.Featurebase('onHide', () => {
        // You can track this if needed for session duration analytics
        console.log('Featurebase messenger was hidden');
      });

      // Track when unread count changes (indicates conversation activity)
      win.Featurebase('onUnreadCountChange', (unreadCount: number) => {
        if (unreadCount > 0) {
          // This indicates a new message was received
          trackConversationEvent('feedback');
        }
      });

      console.log('Featurebase event tracking setup complete');
    } catch (error) {
      console.warn('Error setting up Featurebase event tracking:', error);
      // Retry after a delay if there's an error
      setTimeout(setupEventTracking, 1000);
    }
  };

  const trackConversationEvent = (eventType: 'chat' | 'feedback' | 'help_article' | 'launcher_opened') => {
    try {
      trackEvent('supportConversationStarted', {
        value: eventType,
        metadata: {
          subscriptionPlan: user?.subscriptionPlan,
          pageUrl: window.location.href,
          pagePath: window.location.pathname,
          userId: user?.id
        }
      });
    } catch (error) {
      console.warn('Error tracking Featurebase conversation event:', error);
    }
  };

  return (
    <>
      {/* Load the Featurebase SDK */}
      <Script src="https://do.featurebase.app/js/sdk.js" id="featurebase-sdk" strategy="afterInteractive" />
    </>
  );
};

export default FeaturebaseMessenger;
